import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  DollarSign, GraduationCap, Globe, ExternalLink, ArrowRight,
  Banknote, Award, BookOpen, Building2, Sparkles, CheckCircle
} from 'lucide-react';

const PayingForCollege = () => {
  const internationalScholarships = [
    {
      name: "Fulbright Foreign Student Program",
      description: "Prestigious program for graduate students, young professionals and artists from abroad.",
      link: "https://foreign.fulbrightonline.org/",
      amount: "Full tuition + living expenses",
      countries: "155+ countries"
    },
    {
      name: "AAUW International Fellowships",
      description: "For women pursuing full-time graduate or postdoctoral study in the United States.",
      link: "https://www.aauw.org/resources/programs/fellowships-grants/current-opportunities/international/",
      amount: "$18,000 - $30,000",
      countries: "All non-US women"
    },
    {
      name: "Rotary Foundation Global Grants",
      description: "Scholarships for graduate-level coursework or research in peace, conflict resolution, and more.",
      link: "https://www.rotary.org/en/our-programs/scholarships",
      amount: "$30,000+",
      countries: "Worldwide"
    },
    {
      name: "Joint Japan/World Bank Graduate Scholarship",
      description: "For nationals of World Bank member countries to pursue development-related studies.",
      link: "https://www.worldbank.org/en/programs/scholarships",
      amount: "Full tuition + allowance",
      countries: "World Bank member countries"
    },
    {
      name: "Aga Khan Foundation International Scholarship",
      description: "For outstanding students from developing countries who have no other means of financing.",
      link: "https://www.akdn.org/our-agencies/aga-khan-foundation/international-scholarship-programme",
      amount: "50% grant, 50% loan",
      countries: "Select developing countries"
    },
    {
      name: "Mastercard Foundation Scholars Program",
      description: "Provides comprehensive support for academically talented yet economically disadvantaged students from Africa.",
      link: "https://mastercardfdn.org/all/scholars/",
      amount: "Full financial support",
      countries: "African countries"
    },
    {
      name: "DAAD Scholarships",
      description: "German Academic Exchange Service offers scholarships for international students at US partner universities.",
      link: "https://www.daad.org/en/",
      amount: "Varies",
      countries: "Various"
    },
    {
      name: "IIE SRF (Scholar Rescue Fund)",
      description: "For threatened and displaced scholars who face danger in their home countries or regions.",
      link: "https://www.scholarrescuefund.org/",
      amount: "Varies",
      countries: "Worldwide"
    }
  ];

  const internationalLoans = [
    {
      name: "Prodigy Finance",
      description: "Student loans without collateral or cosigner for masters programs at top universities.",
      link: "https://prodigyfinance.com/",
      features: ["No US cosigner needed", "150+ supported schools", "Competitive rates"],
      maxAmount: "Up to $100,000"
    },
    {
      name: "MPOWER Financing",
      description: "Student loans and scholarships for international and DACA students studying in the US or Canada.",
      link: "https://www.mpowerfinancing.com/",
      features: ["No collateral required", "No cosigner needed", "400+ supported schools"],
      maxAmount: "Up to $100,000"
    },
    {
      name: "Stilt",
      description: "Personal loans for immigrants and international students based on education and career potential.",
      link: "https://www.stilt.com/",
      features: ["Uses visa status", "Education-based approval", "Fast disbursement"],
      maxAmount: "Up to $35,000"
    },
    {
      name: "Ascent Funding",
      description: "Private student loans with cosigner and non-cosigner options for international students.",
      link: "https://www.ascentfunding.com/",
      features: ["Merit-based approval available", "Cosigner release option", "Competitive rates"],
      maxAmount: "Up to cost of attendance"
    },
    {
      name: "Earnest",
      description: "Offers international student loans with a qualified US cosigner.",
      link: "https://www.earnest.com/",
      features: ["No fees", "Flexible payments", "US cosigner required"],
      maxAmount: "Up to $200,000"
    },
    {
      name: "Discover Student Loans",
      description: "International student loans with a creditworthy US citizen or permanent resident as cosigner.",
      link: "https://www.discover.com/student-loans/international/",
      features: ["No origination fees", "Good grades rewards", "US cosigner required"],
      maxAmount: "Up to cost of attendance"
    }
  ];

  const fundingTips = [
    {
      title: "Apply Early & Often",
      description: "Many scholarships have limited funding pools. Submit applications as soon as they open.",
      icon: <Sparkles className="h-6 w-6 text-amber-500" />
    },
    {
      title: "Check University Aid",
      description: "Many US universities offer need-based or merit-based aid specifically for international students.",
      icon: <Building2 className="h-6 w-6 text-blue-500" />
    },
    {
      title: "Government Scholarships",
      description: "Check if your home country's government offers scholarships for studying abroad.",
      icon: <Globe className="h-6 w-6 text-emerald-500" />
    },
    {
      title: "Build Credit History",
      description: "If possible, start building US credit history to improve loan options.",
      icon: <Banknote className="h-6 w-6 text-purple-500" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <DollarSign className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Paying for College</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Financial resources designed specifically for <span className="font-bold">international students</span>
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              As an international student, you may not qualify for US federal financial aid like FAFSA. 
              However, there are numerous scholarships, grants, and loan options specifically designed 
              to help you fund your American education dream.
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
              <div className="text-3xl font-bold text-emerald-600">$8B+</div>
              <div className="text-sm text-gray-600">Annual scholarships available</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
              <div className="text-3xl font-bold text-teal-600">1M+</div>
              <div className="text-sm text-gray-600">International students in US</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
              <div className="text-3xl font-bold text-cyan-600">48%</div>
              <div className="text-sm text-gray-600">Receive some form of aid</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
              <div className="text-3xl font-bold text-blue-600">200+</div>
              <div className="text-sm text-gray-600">Schools offering intl. aid</div>
            </div>
          </div>
        </div>
      </section>

      {/* Scholarships Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <Award className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Scholarships for International Students</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These prestigious scholarships specifically support international students pursuing higher education in the United States.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {internationalScholarships.map((scholarship, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`scholarship-card-${index}`}>
                <CardHeader>
                  <CardTitle className="flex items-start justify-between">
                    <span className="text-lg">{scholarship.name}</span>
                    <a 
                      href={scholarship.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:text-emerald-700"
                      data-testid={`scholarship-link-${index}`}
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{scholarship.description}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                      {scholarship.amount}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {scholarship.countries}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Loans Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Banknote className="h-8 w-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Student Loans for International Students</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Unlike traditional US loans that require citizenship, these lenders specialize in supporting international students — 
              many without requiring a US cosigner.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {internationalLoans.map((loan, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow flex flex-col" data-testid={`loan-card-${index}`}>
                <CardHeader>
                  <CardTitle className="flex items-start justify-between">
                    <span className="text-lg">{loan.name}</span>
                    <a 
                      href={loan.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-700"
                      data-testid={`loan-link-${index}`}
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4">{loan.description}</p>
                  <div className="mt-auto">
                    <ul className="space-y-2 mb-4">
                      {loan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium text-center">
                      {loan.maxAmount}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Funding Tips */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
              <BookOpen className="h-8 w-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Tips for International Students</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Maximize your chances of securing funding with these strategies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fundingTips.map((tip, index) => (
              <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
                  {tip.icon}
                </div>
                <h4 className="font-semibold mb-2">{tip.title}</h4>
                <p className="text-sm text-gray-600">{tip.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-12 bg-amber-50 border-t border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Globe className="h-5 w-5 text-amber-600" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Important Information</h3>
              <p className="text-amber-900/80">
                Financial aid eligibility varies by institution and country of origin. Always verify current 
                requirements directly with scholarship providers and lenders. Interest rates and terms are subject 
                to change. Consider consulting a financial advisor before taking on student loan debt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* University-Specific Aid */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <GraduationCap className="h-12 w-12 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">University-Specific Financial Aid</h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Many top US universities have dedicated financial aid programs for international students. 
            Some even offer need-blind admissions, meaning your financial situation won't affect your admission decision.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="font-bold">Harvard</div>
              <div className="text-sm text-white/80">Need-blind for all</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="font-bold">Yale</div>
              <div className="text-sm text-white/80">Need-blind for all</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="font-bold">MIT</div>
              <div className="text-sm text-white/80">Need-blind for all</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="font-bold">Princeton</div>
              <div className="text-sm text-white/80">Need-blind for all</div>
            </div>
          </div>
          <Link to="/colleges">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-8">
              Explore Universities
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-gray-600 mb-8">
            Create your Wayo account to save colleges, track applications, and get personalized scholarship recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/why-college-matters">
              <Button size="lg" variant="outline" className="px-8">
                Why College Matters
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PayingForCollege;
